import React, { useEffect, useState } from 'react';
import { useStowageStore } from 'src/context/StowageContext';
import { useRotationStore } from 'src/context/RotationContext';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import RotationDropDown from '../RotationDropDown';
import { BlockedTank } from 'src/types/ViewModel/Stowage/BlockedTank';
import { RotationDropDownWrapper } from '../RotationDropDown.styles';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { BlockTankContentWrapper, ElementWrapper, PanelSectionContent } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { createBlockedTankChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/BlockedTankChangeDataFactory';
import { isValidPortCall } from 'src/utils/stowage/stowageRotationUtils';

type Props = {
    tank: Tank;
};

const BlockTankPanel: React.FC<Props> = (props) => {
    const { activeStowagePlan, setIsBlockTankPanelOpen } = useStowageStore();
    const { selectedStowageRotationPortCall, selectedStowageRotationBerthCall, stowageRotationTree } = useRotationStore();
    const [tank, setTank] = useState<Tank | null>(null);
    const [selectedBlockedTank, setSelectedBlockedTank] = useState<BlockedTank | null>(null);
    const [filteredStowageRotationTree, setFilteredStowageRotationTree] = useState<RotationPortCall[]>([]);
    const { saveStowageData } = usePostStowageApi();

    useEffect(() => {
        if (props.tank) {
            setTank(props.tank);
        }

        const _blockedTank: BlockedTank = {
            blockedTankGuid: crypto.randomUUID(),
            tankId: props.tank.tankId,
            tank: props.tank,
            fromPortCallGuid: selectedStowageRotationPortCall?.portCallGuid || null,
            fromPortCall: selectedStowageRotationPortCall || null,
            fromBerthCallGuid: selectedStowageRotationBerthCall?.berthCallGuid || null,
            fromBerthCall: selectedStowageRotationBerthCall || null,
            toPortCallGuid: null,
            toPortCall: null,
            toBerthCallGuid: null,
            toBerthCall: null,
        };

        let _filteredStowageRotationTree = stowageRotationTree;
        if (selectedStowageRotationPortCall && !selectedStowageRotationBerthCall) {
            const _selectedStowageRotationPortCallIndex = stowageRotationTree.findIndex(
                (portCall) => portCall.portCallGuid === selectedStowageRotationPortCall.portCallGuid
            );
            _filteredStowageRotationTree = stowageRotationTree.slice(_selectedStowageRotationPortCallIndex);
        } else if (selectedStowageRotationPortCall && selectedStowageRotationBerthCall) {
            const _selectedStowageRotationPortCallIndex = stowageRotationTree.findIndex(
                (portCall) => portCall.portCallGuid === selectedStowageRotationPortCall.portCallGuid
            );
            _filteredStowageRotationTree = stowageRotationTree.slice(_selectedStowageRotationPortCallIndex);
        }

        _filteredStowageRotationTree = _filteredStowageRotationTree.filter((portCall) => isValidPortCall(portCall));

        setFilteredStowageRotationTree([..._filteredStowageRotationTree]);
        setSelectedBlockedTank(_blockedTank);
    }, []);

    const onSave = async () => {
        if (!activeStowagePlan.blockedTanks) {
            activeStowagePlan.blockedTanks = [];
        }

        if (selectedBlockedTank) {
            const stowagePlanChanges: StowagePlanChange[] = [
                createStowagePlanChange(
                    activeStowagePlan.stowagePlanGuid,
                    selectedBlockedTank.blockedTankGuid,
                    StowageChangeDataType.BlockedTank,
                    createBlockedTankChangeData(selectedBlockedTank)
                ),
            ];
            await saveStowageData(activeStowagePlan, stowagePlanChanges);

            setIsBlockTankPanelOpen(false);
        }
    };

    const isFormDataInvalid = (): boolean => {
        return selectedBlockedTank === null || selectedBlockedTank.toPortCall === null;
    };

    const onSelectedToPortCall = (portCall: RotationPortCall) => {
        if (selectedBlockedTank) {
            const _blockedTank = {
                ...selectedBlockedTank,
                toPortCall: portCall,
                toPortCallGuid: portCall.portCallGuid,
                toBerthCall: null,
                toBerthCallGuid: null,
            };
            setSelectedBlockedTank(_blockedTank);
        }
    };

    const onSelectedToBerthCall = (berthCall: RotationBerthCall) => {
        if (selectedBlockedTank) {
            const _blockedTank = {
                ...selectedBlockedTank,
                toBerthCall: berthCall,
                toBerthCallGuid: berthCall?.berthCallGuid,
                toPortCall: berthCall.portCall,
                toPortCallGuid: berthCall.portCall.portCallGuid,
            };
            setSelectedBlockedTank(_blockedTank);
        }
    };

    const onClosePanel = () => {
        setIsBlockTankPanelOpen(false);
    };

    return (
        tank &&
        selectedBlockedTank && (
            <OdfjellPanel
                headerTitle={'Block tank ' + tank.odfjellTankName}
                defaultPosition="center"
                panelWidth={PANEL_CONSTANTS.MEDIUM_WIDTH}
                onCancel={onClosePanel}
                onAction={onSave}
                showCancelButton={true}
                isActionDisabled={isFormDataInvalid()}
            >
                <BlockTankContentWrapper>
                    <PanelSectionContent>
                        <h4>Blocking tank from</h4>
                        <ElementWrapper>
                            <div>{selectedBlockedTank.fromPortCall?.portDisplayName}</div>
                            <div>{selectedBlockedTank.fromBerthCall?.berthName}</div>
                        </ElementWrapper>
                    </PanelSectionContent>
                    <PanelSectionContent>
                        <h4>Blocking tank to</h4>
                        {selectedBlockedTank?.toPortCall && (
                            <ElementWrapper>
                                <div>{selectedBlockedTank.toPortCall.portDisplayName}</div>
                                <div>{selectedBlockedTank.toBerthCall?.berthName}</div>
                            </ElementWrapper>
                        )}
                        <ElementWrapper>
                            <RotationDropDownWrapper>
                                <RotationDropDown
                                    rotation={filteredStowageRotationTree}
                                    onSelectedPortCall={onSelectedToPortCall}
                                    onSelectedBerthCall={onSelectedToBerthCall}
                                    selectedPortCall={selectedBlockedTank.toPortCall}
                                    selectedBerthCall={selectedBlockedTank.toBerthCall}
                                />
                            </RotationDropDownWrapper>
                        </ElementWrapper>
                    </PanelSectionContent>
                </BlockTankContentWrapper>
            </OdfjellPanel>
        )
    );
};

export default BlockTankPanel;
